import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import debounce from '@dc/ui-library/src/helpers/debounce';

import {
  SuggestionSearchState,
  searchSuggestions,
  setLocation
} from '../../locationSearchHelpers';
import LocationSearchInput from './presentionalComponents/LocationSearchInput';
import LocationsList from './presentionalComponents/LocationsList';
import WrongLocationInputMessage from './presentionalComponents/WrongLocationInputMessage';
import translations from '../../translations';

const PAGE_INFO_FOR_ANALYTICS = 'Listing page';

class LocationSearchInputBlock extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      isTextInputFocused: false,
      containsTextInputTwoOrMoreCharacter: false,
      selectedLocation: null, // this can be from state.foundSuggestions but also from props.previouslyUsedLocations
      foundSuggestions: null,
      isSuggestionsLoading: false
    };

    this.onTextInputChange = debounce(this.onTextInputChange.bind(this), 500);
    this.onTextInputSearchButtonClicked = this.onTextInputSearchButtonClicked.bind(this);
    this.onTextInputFocus = this.onTextInputFocus.bind(this);
    this.onTextInputCleared = this.onTextInputCleared.bind(this);
    this.onTextInputBlur = this.onTextInputBlur.bind(this);
    this.onSuggestionsSearchStateChanged = this.onSuggestionsSearchStateChanged.bind(this);
    this.setFoundSuggestions = this.setFoundSuggestions.bind(this);
    this.setSelectedLocation = this.setSelectedLocation.bind(this);
    this.onListItemMouseEnter = this.onListItemMouseEnter.bind(this);
    this.onListItemClickedOrEntered = this.onListItemClickedOrEntered.bind(this);
    this.onUpKeyClickWhenListItemSelected = this.onUpKeyClickWhenListItemSelected.bind(this);
    this.onDownKeyClickWhenListItemSelected = this.onDownKeyClickWhenListItemSelected.bind(this);
  }

  onTextInputChange(textInputValue) {
    if (textInputValue.length > 1) {
      this.setContainsTextInputTwoOrMoreCharacter(true);
      searchSuggestions(textInputValue, this.onSuggestionsSearchStateChanged);
    } else if (textInputValue.length === 0) {
      this.setContainsTextInputTwoOrMoreCharacter(false);
      this.setFoundSuggestions(null);
    } else {
      this.setContainsTextInputTwoOrMoreCharacter(false);
    }
  }

  onTextInputSearchButtonClicked() {
    setLocation(false, this.state.selectedLocation, PAGE_INFO_FOR_ANALYTICS, this.props.name);
  }

  onTextInputFocus() {
    this.setIsTextInputFocused(true);
  }

  onTextInputBlur() {
    if (this.props.previouslyUsedLocations.includes(this.state.selectedLocation)) {
      // if selectedLocation is from previouslyUsedLocations, after blur diselect location
      this.setSelectedLocation(null);
    }

    this.setIsTextInputFocused(false);
  }

  onTextInputCleared() {
    this.setContainsTextInputTwoOrMoreCharacter(false);
    this.setFoundSuggestions(null);
  }

  onSuggestionsSearchStateChanged(newState, searchTerm, newLocations) {
    switch (newState) {
      case SuggestionSearchState.SEARCHING: {
        this.setIsSuggestionsLoading(true);

        break;
      }
      case SuggestionSearchState.LOCATIONS_FOUND: {
        this.setFoundSuggestions(newLocations);

        if (newLocations[0].location.toLowerCase().startsWith(searchTerm.toLowerCase().trim())) {
          this.setSelectedLocation(newLocations[0]);
        } else {
          this.setSelectedLocation(null);
        }

        this.setIsSuggestionsLoading(false);

        break;
      }
      case SuggestionSearchState.LOCATIONS_NOT_FOUND: {
        this.setFoundSuggestions(null);
        this.setIsSuggestionsLoading(false);

        break;
      }
      default: {
        break;
      }
    }
  }

  onListItemMouseEnter(selectedLocationIndex, selectedLocation) {
    this.setSelectedLocation(selectedLocation);
  }

  onListItemClickedOrEntered(clickedOrEnteredlocationIndex, clickedOrEnteredlocation) {
    setLocation(false, clickedOrEnteredlocation, PAGE_INFO_FOR_ANALYTICS, this.props.name);
  }

  onUpKeyClickWhenListItemSelected(
    selectedLocationIndex, nextSelectedLocationIndex, nextSelectedLocation
  ) {
    this.setSelectedLocation(nextSelectedLocation);
  }

  onDownKeyClickWhenListItemSelected(
    selectedLocationIndex, nextSelectedLocationIndex, nextSelectedLocation
  ) {
    this.setSelectedLocation(nextSelectedLocation);
  }

  setIsTextInputFocused(isFocused) {
    this.setState({
      isTextInputFocused: isFocused
    });
  }

  setContainsTextInputTwoOrMoreCharacter(contains) {
    this.setState({
      containsTextInputTwoOrMoreCharacter: contains
    });
  }

  setSelectedLocation(suggestion) {
    this.setState({
      selectedLocation: suggestion
    });
  }

  setFoundSuggestions(newSuggestions) {
    this.setState({
      foundSuggestions: newSuggestions,
      selectedLocation: null
    });
  }

  setIsSuggestionsLoading(isSuggestionsLoading) {
    this.setState({
      isSuggestionsLoading: isSuggestionsLoading
    });
  }

  renderSuggestionsBlock() {
    const { previouslyUsedLocations } = this.props;
    const {
      foundSuggestions, selectedLocation,
      containsTextInputTwoOrMoreCharacter,
      isSuggestionsLoading
    } = this.state;

    if (foundSuggestions) {
      return (
        <LocationsList
          locations={foundSuggestions}
          selectedLocationIndex={selectedLocation
            ? foundSuggestions.indexOf(selectedLocation)
            : -1
          }
          onListItemMouseEnter={this.onListItemMouseEnter}
          onListItemClickedOrEntered={this.onListItemClickedOrEntered}
          onUpKeyClickWhenListItemSelected={this.onUpKeyClickWhenListItemSelected}
          onDownKeyClickWhenListItemSelected={this.onDownKeyClickWhenListItemSelected}
        />
      );
    } else if (!containsTextInputTwoOrMoreCharacter && previouslyUsedLocations.length > 0) {
      return (
        <LocationsList
          headerLabel={translations.previousSearchesLabel}
          locations={previouslyUsedLocations}
          selectedLocationIndex={selectedLocation
            ? previouslyUsedLocations.indexOf(selectedLocation)
            : -1
          }
          onListItemMouseEnter={this.onListItemMouseEnter}
          onListItemClickedOrEntered={this.onListItemClickedOrEntered}
          onUpKeyClickWhenListItemSelected={this.onUpKeyClickWhenListItemSelected}
          onDownKeyClickWhenListItemSelected={this.onDownKeyClickWhenListItemSelected}
        />
      );
    } else if (containsTextInputTwoOrMoreCharacter && !isSuggestionsLoading) {
      return (
        <WrongLocationInputMessage
          message={translations.wrongLocationInputMessage}
        />
      );
    }

    return null;
  }

  render() {
    const { disabled, name, isMobile } = this.props;
    const {
      selectedLocation, isTextInputFocused,
      containsTextInputTwoOrMoreCharacter, foundSuggestions
    } = this.state;

    let searchInputState;

    if (!foundSuggestions && isTextInputFocused && containsTextInputTwoOrMoreCharacter) {
      searchInputState = 'invalid';
    } else if (selectedLocation) {
      searchInputState = 'valid';
    } else {
      searchInputState = 'neutral';
    }

    return (
      <div className="dc-location-search-input-block">
        <LocationSearchInput
          name={name}
          placeholderLabel={translations.postcodeCheckerPlaceholder}
          disabledLabel={translations.postcodeCheckerPending}
          loadingAndDisabled={disabled}
          onTextInputBlur={this.onTextInputBlur}
          onTextInputChange={this.onTextInputChange}
          onTextInputFocus={this.onTextInputFocus}
          onClearButtonClick={this.onTextInputCleared}
          onEscKeyClick={this.onTextInputCleared}
          onSearchButtonClick={this.onTextInputSearchButtonClicked}
          highlightedInputTextSuffix={(!isMobile && selectedLocation)
            ? selectedLocation.location
            : ''}
          searchInputState={searchInputState}
        />
        {isTextInputFocused && !disabled &&
          this.renderSuggestionsBlock()
        }
      </div>
    );
  }
}

LocationSearchInputBlock.propTypes = {
  // DON'T CHANGE THE VALUES of the following prop, the format is due to analytics
  name: PropTypes.oneOf([
    'above product listing',
    'filter block'
  ]).isRequired,
  disabled: PropTypes.bool.isRequired,
  previouslyUsedLocations: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      retailLocation: PropTypes.bool
    })
  ).isRequired,
  isMobile: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  return {
    previouslyUsedLocations: state.listingPage.locationSearch.previouslyUsedLocations,
    isMobile: state.general.device.deviceType === 'mobile'
  };
}

export default connect(mapStateToProps)(LocationSearchInputBlock);
